<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.groups', 2),
                        href: meta.links.index,
                    },
                    {
                        name: group.name,
                        href: null,
                    },
                ]"
            >
                <template v-slot:corner>
                    <div class="flex space-x-1 items-center">
                        <BadgePill>
                            <InertiaLink :href="meta.links.graphs">{{ $t('messages.graphs') }}</InertiaLink>
                        </BadgePill>
                        <BadgePill>
                            <InertiaLink :href="meta.links.grades_overview">{{ $t('messages.graphs') }} 2</InertiaLink>
                        </BadgePill>
                        <ActivityMenuOptions>
                            <InertiaLink
                                v-for="otherGroup in meta.links.other_groups"
                                :href="otherGroup.url"
                                :key="otherGroup.url"
                                class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                role="menuitem"
                            >
                                {{ otherGroup.text }}
                            </InertiaLink>
                            <InertiaLink
                                :href="meta.links.edit"
                                class="text-indigo-400 block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                role="menuitem"
                            >
                                {{ $t('messages.edit') }} ({{ $t('messages.image') }})
                            </InertiaLink>
                        </ActivityMenuOptions>
                    </div>
                </template>
            </SmartBreadcrumbs>
        </template>

        <div>
            <div v-if="options.practice_mode_settings.settings.is_active">
                <AlertMessage class="mt-5 inline-block" type="info">
                    <template v-slot:title>
                        <span>{{ options.practice_mode_settings.settings.mode_display }}.</span>
                    </template>
                    <template v-slot:body>
                        <div class="ml-3 text-lg">
                            {{ $t('messages.time_left') }}
                            <span>
                                <CountdownTimer
                                    class="inline-block"
                                    :datetime="options.practice_mode_settings.settings.ends_at"
                                />
                            </span>
                        </div>
                    </template>
                </AlertMessage>
            </div>
        </div>

        <div class="flex justify-center">
            <div class="h-1 w-1" id="particles-source"></div>
            <div id="particles-origin" class="relative h-6 w-6" />
        </div>

        <div class="w-full flex justify-end space-x-2 flex-wrap">
            <div class="flex space-x-2">
                <div>
                    <aside class="justify-self-center">
                        <nav
                            class="inline-flex flex-wrap items-center justify-center p-1 space-x-1 overflow-hidden text-sm text-gray-600 bg-gray-100 rounded-xl"
                        >
                            <button class="p-1" @click="toggleMode()">
                                <svg
                                    class="w-6 h-6"
                                    v-show="mode === 'team'"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"
                                    ></path>
                                </svg>
                                <svg
                                    class="w-6 h-6"
                                    v-show="mode === 'individual'"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                            </button>
                        </nav>
                    </aside>
                </div>
            </div>

            <ActivityModeGroupPanel :settings="options.practice_mode_settings" />

            <div v-for="link in links" :key="link.href">
                <AppButton
                    :href="link.href"
                    :element="link.element ?? 'InertiaLink'"
                    :desired-action="link.desired_action"
                >
                    {{ link.text }}
                </AppButton>
            </div>
            <AppButton
                :href="meta.links.group_factions_show"
                v-if="meta.links.group_factions_show"
                element="InertiaLink"
            >
                {{ $tc('messages.factions', 2) }}
            </AppButton>
            <AppButton :href="meta.links.group_faction_race" v-if="meta.links.group_faction_race" element="InertiaLink">
                {{ $tc('messages.practice_races', 1) }}
            </AppButton>
            <!--            <WeeklyRewardNotification />-->
        </div>

        <div>
            <div class="w-full flex justify-end space-x-2 flex-wrap mt-5 items-end">
                <div>
                    <div class="md:grid md:grid-cols-6 md:gap-6">
                        <div class="mt-1">
                            <FormLabel :value="$tc('messages.gamification_character_types', 2)" />
                            <SlideOverCheckboxInput
                                v-model="characterTypesSelected"
                                :options="options.characterTypes"
                            />
                        </div>

                        <div class="sm:col-span-3" v-show="mode === 'individual'">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.show')" for="show" />
                                <FormSelect
                                    id="grouping"
                                    v-model="settings.show"
                                    :options="showOptions"
                                    class="mt-1 w-full"
                                    :null-option="false"
                                />
                            </div>
                        </div>

                        <div class="sm:col-span-1">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.photo')" for="show" />
                                <FormSelect
                                    id="avatar"
                                    v-model="settings.avatar"
                                    :options="avatarOptions"
                                    class="mt-1 w-full"
                                    :null-option="false"
                                />
                            </div>
                        </div>

                        <div class="sm:col-span-2">
                            <div class="mt-1">
                                <FormLabel :value="$tc('messages.columns', 2)" for="show" />
                                <FormSelect
                                    id="avatar"
                                    v-model="settings.columns"
                                    :options="columnOptions"
                                    class="mt-1 w-full"
                                    :null-option="false"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div v-show="selectedStudents.length > 0">
                    <GiveGroupCurrencyActionsPanel
                        :students="selectedStudents"
                        :gamification-set="group.gamificationSet"
                        :items="options.items"
                    />
                </div>

                <AppButton type="button" element="button" v-if="group.battles.length" @click="openBattlesPanel = true">
                    {{ $tc('messages.battles', group.battles.length) }}
                    <SlideOver
                        :title="$tc('messages.battles', 2)"
                        :show="openBattlesPanel"
                        @close="openBattlesPanel = false"
                    >
                        <div class="divide-y-2 divide-gray-100">
                            <div class="flex justify-between py-2" v-for="battle in group.battles" :key="battle.id">
                                <div>
                                    <img :src="battle.enemy_image" alt="" class="h-12 inline-block" />
                                    {{ battle.name }}
                                </div>
                                <div>
                                    <InertiaLink
                                        method="patch"
                                        v-if="battle.links.give_reward"
                                        :href="battle.links.give_reward"
                                        :title="$t('messages.give_reward')"
                                        as="button"
                                        class="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                    >
                                        <svg
                                            class="w-4 h-4"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M4.555 5.168A1 1 0 003 6v8a1 1 0 001.555.832L10 11.202V14a1 1 0 001.555.832l6-4a1 1 0 000-1.664l-6-4A1 1 0 0010 6v2.798l-5.445-3.63z"
                                            ></path>
                                        </svg>
                                    </InertiaLink>
                                    <button
                                        v-else
                                        class="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                                    >
                                        <svg
                                            class="w-4 h-4"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M4.555 5.168A1 1 0 003 6v8a1 1 0 001.555.832L10 11.202V14a1 1 0 001.555.832l6-4a1 1 0 000-1.664l-6-4A1 1 0 0010 6v2.798l-5.445-3.63z"
                                            ></path>
                                        </svg>
                                    </button>
                                    <InertiaSquareLinkButton action="play" :href="battle.links.play" />
                                </div>
                            </div>
                        </div>
                    </SlideOver>
                </AppButton>

                <SelectAllNoneButtons
                    :max-count="students.length"
                    :selected-count="selectedStudents.length"
                    @select-all="selectAll()"
                    @select-none="deselectAll()"
                    class="flex"
                />

                <AppButton @click="selectPresent()" type="button" element="button">
                    {{ $t('messages.select_present_students') }}
                </AppButton>
            </div>

            <div v-show="mode === 'team'" class="mt-6 grid grid-cols-1 gap-y-2 gap-x-4 sm:grid-cols-2 md:grid-cols-3">
                <div v-for="(team, index) in group.teams" class="bg-gray-200 p-2 rounded-lg" :key="team.id">
                    <div class="flex justify-between">
                        <div>
                            {{ team.name || index + 1 }}
                        </div>
                        <div>
                            <GiveGroupCurrencyActionsPanel
                                :students="team.students"
                                :gamification-set="group.gamificationSet"
                                :items="options.items"
                                square-button
                            />
                        </div>
                    </div>
                    <div class="flex -space-x-2 relative z-0 overflow-hidden p-2 bg-white rounded-lg">
                        <img
                            v-for="(student, index) in team.students"
                            :key="index"
                            :class="{
                                'opacity-50': student.absent && settings.show === 'distinguish_absent_students',
                            }"
                            class="relative z-30 inline-block h-16 w-16 rounded-full ring-2 ring-white"
                            :src="student[settings.avatar]"
                            alt=""
                        />
                    </div>
                </div>
            </div>

            <div
                v-show="mode === 'individual'"
                class="mt-6 grid grid-cols-1 gap-y-2 gap-x-4 sm:grid-cols-2"
                :class="settings.columns"
            >
                <div
                    class="relative col-span-1 flex shadow rounded-md"
                    v-for="student in filteredStudents"
                    :key="student.id"
                    :class="{
                        'opacity-50': student.absent && settings.show === 'distinguish_absent_students',
                    }"
                >
                    <div
                        class="shrink-0 flex items-center justify-center w-16 text-white text-sm leading-5 font-medium rounded-l-md overflow-hidden"
                    >
                        <a :href="student.links.show">
                            <img :src="student[settings.avatar]" alt="profile photo" />
                        </a>
                    </div>
                    <div
                        class="flex-1 flex flex-col justify-start border-t border-r border-b border-gray-200 bg-white rounded-r-md truncate"
                    >
                        <div class="flex flex-1 justify-between px-4 pt-1 text-sm leading-5 truncate">
                            <div class="flex gap-1">
                                <a
                                    :href="student.links.show"
                                    class="text-gray-900 font-medium hover:text-gray-600 transition ease-in-out duration-150"
                                >
                                    {{ student.index }}. {{ student.full_name }}
                                    <span v-if="student.gamificationScoreCard.show_faint_icon"> ☠️ </span>
                                </a>
                                <div v-if="student.is_duel_week_winner">
                                    <div :title="meta.info.last_week_duel_winner.message">
                                        <TrophyIcon class="text-yellow-400 h-6 w-6" />
                                    </div>
                                </div>
                            </div>
                            <div>
                                <GiveCurrencyActionsPanel
                                    :student="student"
                                    :gamification-set="group.gamificationSet"
                                    :items="options.items"
                                />
                                <input
                                    name="selected"
                                    v-model="student.selected"
                                    type="checkbox"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                            </div>
                        </div>
                        <div class="flex">
                            <GamificationScoreCard
                                :gamification-score-card="student.gamificationScoreCard"
                                :id="`student_${student.id}`"
                                :settings="group.gamificationSet.card_settings"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="flex justify-center mt-5">
            <img v-if="group.image_path" class="w-40 object-contain" :src="group.image_path" alt="" />
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import BadgePill from '@/components/BadgePill.vue';
import GamificationScoreCard from '@/components/Gamification/GamificationScoreCard.vue';
import AppButton from '@/components/AppButton.vue';
import GiveCurrencyActionsPanel from '@/components/Gamification/GiveCurrencyActionsPanel.vue';
import GiveGroupCurrencyActionsPanel from '@/components/Gamification/GiveGroupCurrencyActionsPanel.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import SelectAllNoneButtons from '@/components/SelectAllNoneButtons.vue';
import SlideOverCheckboxInput from '@/components/Forms/SlideOverCheckboxInput.vue';
import ActivityMenuOptions from '@/components/pageSpecific/ActivityMenuOptions.vue';
import ActivityModeGroupPanel from '@/components/ActivityModeGroupPanel.vue';
import CountdownTimer from '@/components/CountdownTimer.vue';
import AlertMessage from '@/components/AlertMessage.vue';
import SlideOver from '@/components/SlideOver.vue';
import TrophyIcon from '@/components/Gamification/TrophyIcon.vue';

export default {
    components: {
        TrophyIcon,
        SlideOver,
        AlertMessage,
        CountdownTimer,
        ActivityModeGroupPanel,
        PanelPage,
        FormLabel,
        BadgePill,
        GamificationScoreCard,
        AppButton,
        GiveCurrencyActionsPanel,
        GiveGroupCurrencyActionsPanel,
        InertiaSquareLinkButton,
        FormSelect,
        SlideOverCheckboxInput,
        SelectAllNoneButtons,
        ActivityMenuOptions,
    },
    layout: Layout,
    name: 'Show',
    props: ['teacher', 'meta', 'options', 'group', 'students', 'links'],
    data() {
        return {
            show: false,
            openBattlesPanel: false,
            student: null,
            points: 10,
            characterTypesSelected: [],
            comment: '',
            mode: 'individual',
            settings: {
                show: 'distinguish_absent_students',
                avatar: 'avatar_or_profile_photo',
                columns: 'md:grid-cols-4 lg:grid-cols-4',
            },
            showOptions: [
                {
                    value: 'all',
                    text: this.$t('messages.all'),
                },
                {
                    value: 'distinguish_absent_students',
                    text: this.$t('messages.distinguish_absent_students'),
                },
                {
                    value: 'hide_absent_students',
                    text: this.$t('messages.hide_absent_students'),
                },
            ],
            avatarOptions: [
                {
                    value: 'avatar_or_profile_photo',
                    text: this.$t('messages.avatar'),
                },
                {
                    value: 'avatar_or_default',
                    text: this.$t('messages.photo'),
                },
            ],
            columnOptions: [
                {
                    value: 'md:grid-cols-2 lg:grid-cols-2',
                    text: 2,
                },
                {
                    value: 'md:grid-cols-3 lg:grid-cols-3',
                    text: 3,
                },
                {
                    value: 'md:grid-cols-4 lg:grid-cols-4',
                    text: 4,
                },
                {
                    value: 'md:grid-cols-5 lg:grid-cols-5',
                    text: 5,
                },
                {
                    value: 'md:grid-cols-6 lg:grid-cols-6',
                    text: 6,
                },
            ],
        };
    },
    watch: {
        'settings.show': {
            handler(newVal) {
                if (newVal !== 'all') {
                    this.deselectAbsent();
                }
                localStorage.setItem('Teacher/Gamification/Groups/Show_settings_show', newVal);
            },
            deep: true,
        },
        'settings.avatar': {
            handler(newVal) {
                localStorage.setItem('Teacher/Gamification/Groups/Show_settings_avatar', newVal);
            },
            deep: true,
        },
        'settings.columns': {
            handler(newVal) {
                localStorage.setItem('Teacher/Gamification/Groups/Show_settings_columns', newVal);
            },
            deep: true,
        },
    },
    methods: {
        toggleMode() {
            this.mode = this.mode === 'team' ? 'individual' : 'team';
        },
        showSlideOver(student) {
            this.student = student;
            this.show = true;
        },
        closeSlideOver() {
            this.show = false;
            this.student = null;
        },
        selectAll() {
            this.filteredStudents.forEach((s) => (s.selected = true));
        },
        selectPresent() {
            this.students.forEach((s) => (s.selected = !s.absent));
        },
        deselectAbsent() {
            this.students.filter((s) => s.absent).forEach((s) => (s.selected = false));
        },
        deselectAll() {
            this.students.forEach((s) => (s.selected = false));
        },
    },
    computed: {
        selectedStudents: function () {
            return this.students.filter((s) => s.selected);
        },
        filteredStudents: function () {
            return this.students
                .filter((s) => {
                    if (this.characterTypesSelected.length) {
                        return this.characterTypesSelected.includes(
                            s.gamificationScoreCard.gamification_character_type_id,
                        );
                    }
                    return true;
                })
                .filter((s) => {
                    if (this.settings.show === 'hide_absent_students') {
                        return !s.absent;
                    }
                    return true;
                });
        },
    },
    mounted() {
        const settingsAvatar = localStorage.getItem('Teacher/Gamification/Groups/Show_settings_avatar');
        if (settingsAvatar) {
            this.settings.avatar = settingsAvatar;
        }
        const settingsShow = localStorage.getItem('Teacher/Gamification/Groups/Show_settings_show');
        if (settingsShow) {
            this.settings.show = settingsShow;
        }
        const settingsColumns = localStorage.getItem('Teacher/Gamification/Groups/Show_settings_columns');
        if (settingsColumns) {
            this.settings.columns = settingsColumns;
        }
    },
};
</script>
